import { useState, useEffect } from "react";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Card,
    CardBody,
    Autocomplete,
    AutocompleteItem,
    Chip,
    Input,
} from "@heroui/react";
import { Filter, X, Check, Search } from "lucide-react";
import { useUniversities } from "@src/presentation/hooks/universities";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import useScreenSize, { ScreenSize } from "@src/presentation/hooks/useScreenSize";

interface FilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApplyFilters: (filters: FilterState) => void;
    initialFilters: FilterState;
    totalResults: number;
    filterOptions: {
        semesters: string[];
        academicYears: string[];
    };
}

export interface FilterState {
    searchQuery?: string;
    semester: string;
    academicYear: string;
    university: string;
}

export function FilterModal({
    isOpen,
    onClose,
    onApplyFilters,
    initialFilters,
    totalResults,
    filterOptions,
}: FilterModalProps) {
    const { isAuthenticated } = useAuth();
    const screenSize = useScreenSize();
    const isMobile = screenSize === ScreenSize.Mobile;

    // Local state for filters
    const [filters, setFilters] = useState<FilterState>(initialFilters);

    // Fetch universities for filter
    const { data: universitiesData, isLoading: isLoadingUniversities } = useUniversities();

    // Update local state when initial filters change
    useEffect(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    useEffect(() => {
        if (!isOpen || !isMobile) return;

        // Lock body scroll and prevent viewport changes
        const originalOverflow = document.body.style.overflow;
        const originalPosition = document.body.style.position;

        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        const handleVisualViewportResize = () => {
            const modal = document.querySelector('[role="dialog"]');
            if (modal instanceof HTMLElement) {
                const wrapper = modal.parentElement;
                if (wrapper) {
                    wrapper.style.position = 'fixed';
                    wrapper.style.height = '100vh';
                    wrapper.style.height = '100dvh'; // Use dynamic viewport height
                }
                modal.style.position = 'fixed';
                modal.style.bottom = '0';
                modal.style.top = 'auto';
            }
        };

        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleVisualViewportResize);
            handleVisualViewportResize();
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.position = originalPosition;
            document.body.style.width = '';

            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
            }
        };
    }, [isOpen, isMobile]);

    const handleFilterChange = (key: keyof FilterState, value: string) => {
        const newFilters = { ...filters, [key]: value };
        setFilters(newFilters);
        onApplyFilters(newFilters);
    };

    const handleApplyFilters = () => {
        onClose();
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            searchQuery: isMobile ? filters.searchQuery : "", // Keep search on mobile, clear on desktop
            semester: "",
            academicYear: "",
            university: "",
        };
        setFilters(clearedFilters);
        onApplyFilters(clearedFilters);
    };

    const hasActiveFilters =
        filters.semester ||
        filters.academicYear ||
        filters.university;

    const activeFilterCount = [
        filters.semester,
        filters.academicYear,
        filters.university,
    ].filter(Boolean).length;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? "full" : "lg"}
            scrollBehavior="inside"
            placement={isMobile ? "bottom" : "center"}
            classNames={{
                base: isMobile
                    ? "m-0 rounded-t-xl max-h-[80vh] h-auto"
                    : "rounded-xl",
                body: isMobile
                    ? "p-4 overflow-y-auto"
                    : "p-6",
                header: isMobile
                    ? "border-b border-border p-4"
                    : "border-b border-border p-6",
                footer: isMobile
                    ? "border-t border-border p-4 bg-background/95 backdrop-blur-sm"
                    : "border-t border-border p-6",
            }}
            backdrop={isMobile ? "blur" : "opaque"}
        >
            <ModalContent>
                <ModalHeader className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <Filter className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">Filters</h2>

                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {hasActiveFilters && (
                            <Chip size="sm" variant="flat" color="primary">
                                {activeFilterCount} active
                            </Chip>
                        )}
                        {hasActiveFilters && (
                            <Button
                                size="sm"
                                variant="light"
                                color="danger"
                                onPress={handleClearFilters}
                                startContent={<X className="h-4 w-4" />}
                                className="text-sm"
                            >
                                Clear All
                            </Button>
                        )}
                    </div>
                </ModalHeader>

                <ModalBody className="overflow-y-auto">
                    <div className="space-y-6">
                        {/* Search Input - Only show on desktop, on mobile it's on the main page */}
                        {!isMobile && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Search</label>
                                <Input
                                    type="text"
                                    placeholder="Search by course name or code..."
                                    value={filters.searchQuery || ""}
                                    onValueChange={(value) => handleFilterChange("searchQuery", value)}
                                    startContent={<Search className="h-4 w-4 text-foreground/60" />}
                                    isClearable
                                    onClear={() => handleFilterChange("searchQuery", "")}
                                    radius="none"
                                    classNames={{
                                        base: 'focus-within:ring-2 focus-within:ring-primary',
                                        inputWrapper: 'border border-border',
                                    }}
                                />
                            </div>
                        )}

                        {/* Semester Filter */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Semester</label>
                            <Autocomplete
                                placeholder="All semesters"
                                radius="none"
                                selectedKey={filters.semester}
                                onSelectionChange={(key) => handleFilterChange("semester", key as string)}
                                classNames={{
                                    popoverContent: "rounded border border-border",
                                    base: "focus-within:ring-2 focus-within:ring-primary border border-border",
                                }}
                            >
                                {filterOptions.semesters.map((semester) => (
                                    <AutocompleteItem className="rounded" key={semester}>
                                        {semester === "1" ? "1st Semester" : semester === "2" ? "2nd Semester" : semester === "3" ? "3rd Semester" : `${semester} Semester`}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                        </div>

                        {/* Academic Year Filter */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Academic Year</label>
                            <Autocomplete
                                placeholder="All years"
                                radius="none"
                                selectedKey={filters.academicYear}
                                onSelectionChange={(key) => handleFilterChange("academicYear", key as string)}
                                classNames={{
                                    popoverContent: "rounded border border-border",
                                    base: "focus-within:ring-2 focus-within:ring-primary border border-border",
                                }}
                            >
                                {filterOptions.academicYears.map((year) => (
                                    <AutocompleteItem className="rounded" key={year}>
                                        {year}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>
                        </div>

                        {/* University Filter - Only show for unauthenticated users */}
                        {!isAuthenticated && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">University</label>
                                <Autocomplete
                                    placeholder="All universities"
                                    radius="none"
                                    selectedKey={filters.university}
                                    onSelectionChange={(key) => handleFilterChange("university", key as string)}
                                    isLoading={isLoadingUniversities}
                                    classNames={{
                                        popoverContent: "rounded border border-border",
                                        base: "focus-within:ring-2 focus-within:ring-primary border border-border",
                                    }}
                                >
                                    {(universitiesData?.universities || []).map((university) => (
                                        <AutocompleteItem className="rounded" key={university.university_id}>
                                            {university.code}
                                        </AutocompleteItem>
                                    ))}
                                </Autocomplete>
                            </div>
                        )}

                        {/* Active Filters Display */}
                        {hasActiveFilters && (
                            <Card className="border border-border bg-card rounded">
                                <CardBody className="p-4">
                                    <h4 className="text-sm font-medium text-foreground mb-3">Active Filters</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {filters.semester && (
                                            <Chip size="sm" variant="flat" color="primary">
                                                Semester: {filters.semester === "1" ? "1st" : filters.semester === "2" ? "2nd" : filters.semester === "3" ? "3rd" : filters.semester}
                                            </Chip>
                                        )}
                                        {filters.academicYear && (
                                            <Chip size="sm" variant="flat" color="primary">
                                                Year: {filters.academicYear}
                                            </Chip>
                                        )}
                                        {filters.university && (
                                            <Chip size="sm" variant="flat" color="primary">
                                                University: {universitiesData?.universities?.find(u => u.university_id === filters.university)?.code || filters.university}
                                            </Chip>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </ModalBody>

                <ModalFooter className="flex-col sm:flex-row gap-2">
                    <Button
                        variant="light"
                        onPress={onClose}
                        className="flex-1 sm:flex-initial"
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        size="lg"
                        onPress={handleApplyFilters}
                        startContent={<Check className="h-5 w-5" />}
                        className="flex-1 sm:flex-initial font-semibold min-h-[48px] px-6 rounded"
                    >
                        Show {hasActiveFilters ? `${totalResults} ${totalResults === 1 ? "Result" : "Results"}` : "Results"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}